import { useEffect, useState } from "react";
import { Rocket, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

function Step({ ok, title, children, testid }) {
  return (
    <li className="flex items-start gap-3" data-testid={testid}>
      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />}
      <div className="min-w-0 flex-1">
        <div className={`font-medium ${ok ? "text-slate-200" : "text-amber-100"}`}>{title}</div>
        <div className="text-xs text-slate-400 mt-0.5">{children}</div>
      </div>
    </li>
  );
}

/** Go-live checklist: what still points at the preview and whether Stripe is live. */
export function GoLiveChecklist() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/golive").then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { load(); }, []);
  if (d === null) return <div className="text-slate-500 text-sm">Checking go-live status…</div>;
  if (!d) return null;
  const stripeLive = d.stripe?.mode === "live" && d.stripe?.charges_enabled;
  const allGood = !d.is_preview && stripeLive;

  return (
    <div className="dg-card rounded-xl p-5" data-testid="golive-checklist">
      <div className="flex items-center gap-2">
        <Rocket className="w-4 h-4 text-[#FFD600]" />
        <div className="dg-eyebrow">Go-live checklist</div>
        <span className={`dg-chip ml-auto ${allGood ? "text-emerald-300" : "text-amber-300"}`} data-testid="golive-chip">{allGood ? "Live and taking payments" : d.is_preview ? "Running on preview" : "Needs attention"}</span>
        <button type="button" onClick={load} className="dg-link text-xs flex items-center gap-1" data-testid="golive-refresh"><RefreshCw className="w-3 h-3" /> Re-check</button>
      </div>
      <p className="text-sm text-slate-400 mt-2">Press <strong className="text-slate-200">Deploy</strong> in the Emergent chat first, then work down this list.</p>
      <ol className="mt-4 space-y-3 text-sm">
        <Step ok={!d.is_preview} title={`1 · PUBLIC_APP_URL ${d.is_preview ? "still points at the preview" : "points at your live domain"}`} testid="golive-url">
          Current: <code className="text-cyan-300">{d.public_app_url}</code>. After Deploy, set the backend variable <code>PUBLIC_APP_URL</code> to your live address (e.g. https://docdrgadget.com). It drives every emailed link, including payment links.
        </Step>
        <Step ok={stripeLive} title={`2 · Stripe ${stripeLive ? "is live" : d.stripe?.charges_enabled ? "is ready — switches to live on deploy" : "is in test mode"}`} testid="golive-stripe">
          {stripeLive
            ? <>Real cards are being charged. Payouts {d.stripe.payouts_enabled ? "enabled" : "pending in your Stripe dashboard"}.</>
            : <>Claim your Stripe account from Emergent → <strong>Manage → Payments</strong> (one click, then Stripe's identity check). Until then every checkout is a test — card 4242 4242 4242 4242 works.</>}
        </Step>
        <Step ok title="3 · Scheduled emails" testid="golive-crons">
          {d.crons.length} platform schedules run automatically once deployed: {d.crons.join(", ")}. Watch the first ones land in {d.owner_emails.join(" / ")}.
        </Step>
        <Step ok title="4 · First real session" testid="golive-first">
          Book yourself a session on /book, then from Admin → Bookings press <strong>Send payment link</strong> and pay it with your own card to see the whole loop: email → Stripe checkout → Paid chip → receipt.
        </Step>
      </ol>
    </div>
  );
}
